import { useCallback, useMemo, useState } from 'react'

import { MultiSelect, Select } from 'govwell-ui'
import { SelectBaseProps, SelectModeProps, SelectOption } from 'govwell-ui/components/Select/types'
import { observer } from 'mobx-react-lite'
import { UserDataFragment, UserType } from 'types/graphql'

import useDebounce from 'src/hooks/use-debounce'
import { filterNil, formatUserLabel } from 'src/utils'

import { useSearchUsersQuery } from '../../fetch/users'

type Props = SelectModeProps<UserDataFragment> & {
  caption?: React.ReactNode
  customDisplay?: (user: UserDataFragment) => React.ReactNode
  errorMessage?: string
  getIsOptionDisabled?: (option: SelectOption<UserDataFragment>) => boolean
  includeUsersFromAllOrganizations?: boolean
  initialOptions?: SelectOption<UserDataFragment>[]
  isClearable?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  placeholder?: string
  recordUuids?: string[]
  type?: UserType
  userIdToGroupLabel?: Map<number, string>
  width?: React.CSSProperties['width']
}

const UserSearch = ({
  caption,
  customDisplay,
  errorMessage,
  getIsOptionDisabled,
  includeUsersFromAllOrganizations,
  initialOptions,
  isClearable,
  isInvalid,
  isMultiSelect,
  isRequired,
  label,
  onBlur,
  onValueChange,
  placeholder,
  recordUuids,
  type,
  userIdToGroupLabel,
  value,
  width,
}: Props) => {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)
  const { data, isFetching } = useSearchUsersQuery({
    query: debouncedQuery,
    input: {
      includeAllOrgs: includeUsersFromAllOrganizations,
      recordUuids,
      type,
    },
  })

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<UserDataFragment>[] | undefined) => {
      if (isMultiSelect) {
        onValueChange(newSelectedOptions?.map((o) => o.value) ?? [])
      }
    },
    [isMultiSelect, onValueChange]
  )

  const handleSelectedOptionChange = useCallback(
    (newSelectedOption: SelectOption<UserDataFragment> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(newSelectedOption?.value)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const valueAsArray = useMemo(() => (Array.isArray(value) ? value : filterNil([value])), [value])

  const options = useMemo(() => {
    const selectedUserIds = new Set<number>(valueAsArray.map((u) => u.id))
    const toOption = (user: UserDataFragment, group?: string): SelectOption<UserDataFragment> => ({
      customDisplay,
      group,
      label: formatUserLabel(user),
      value: user,
    })
    const selected = valueAsArray.map((u) => toOption(u))

    if (initialOptions && !debouncedQuery) {
      return [...selected, ...initialOptions.filter((o) => !selectedUserIds.has(o.value.id))]
    }

    const searchResults = (data?.searchUsers ?? []).filter((u) => !selectedUserIds.has(u.id))
    return [
      ...selected,
      ...searchResults.map((u) =>
        toOption(u, userIdToGroupLabel?.get(u.id) ?? (userIdToGroupLabel ? 'Other' : undefined))
      ),
    ]
  }, [
    customDisplay,
    data?.searchUsers,
    debouncedQuery,
    initialOptions,
    userIdToGroupLabel,
    valueAsArray,
  ])

  const selectedOptionSingleSelect = useMemo(() => {
    if (!isMultiSelect) {
      return options.find((o) => o.value === value)
    }
  }, [isMultiSelect, options, value])

  const selectedOptions = useMemo(
    () => options.filter((o) => valueAsArray.includes(o.value)),
    [options, valueAsArray]
  )

  const getOptionKey = useCallback((option: SelectOption<UserDataFragment>) => option.value.id, [])

  const commonProps: SelectBaseProps<UserDataFragment> = {
    ...(label ? { label } : { ariaLabel: 'User' }),
    caption,
    errorMessage,
    getIsOptionDisabled,
    getOptionKey,
    isAsync: true,
    isClearable,
    isInvalid,
    isLoading: isFetching,
    isRequired,
    width,
    onBlur,
    options,
    placeholder: placeholder ?? 'Search for users by name, company, email or phone',
    onSearchQueryChange: setQuery,
  }

  if (isMultiSelect) {
    return (
      <MultiSelect
        selectedOptions={selectedOptions}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        {...commonProps}
      />
    )
  }

  return (
    <Select
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOptionSingleSelect}
      {...commonProps}
    />
  )
}

export default observer(UserSearch)
